import { CheckCircle2, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { downloadMedia } from "@/lib/mediaUtils";
import { PreviewSkeletonTile } from "@/components/flowly/PreviewSkeletonTile/PreviewSkeletonTile";

interface TalkingAvatarPreviewGridProps {
  /** Display number — shifts with the chosen image mode path. */
  stepNumber: number;
  imageUrls: string[];
  selectedIndex: number | null;
  isGenerating: boolean;
  /** ISO timestamp captured when the active generation started — drives the
   *  progress bar's elapsed-time curve. Required while `isGenerating`. */
  generationStartedAt: string | null;
  onSelect: (index: number) => void;
}

export function TalkingAvatarPreviewGrid({
  stepNumber,
  imageUrls,
  selectedIndex,
  isGenerating,
  generationStartedAt,
  onSelect,
}: TalkingAvatarPreviewGridProps) {
  const { t } = useLanguage();

  // Show the cursor hint only when both previews are ready AND the user
  // hasn't picked yet. Once a card is selected, the animation goes away.
  const showCursorHint = imageUrls.length === 2 && selectedIndex === null;

  return (
    <div className="space-y-4 md:space-y-5">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.talkingAvatar.stepPrefix.replace("{n}", String(stepNumber))}
          {t.talkingAvatar.previewPickerTitle}
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
          {t.talkingAvatar.previewPickerHint}
        </p>
      </div>

      <div className="relative grid grid-cols-2 gap-3 md:gap-4 max-w-md mx-auto">
        {imageUrls.map((url, idx) => {
          const isSelected = selectedIndex === idx;
          return (
            <div key={url} className="relative">
              <button
                type="button"
                onClick={() => onSelect(idx)}
                aria-pressed={isSelected}
                className={cn(
                  "relative aspect-[3/4] w-full rounded-2xl overflow-hidden border-2 transition-all duration-200",
                  isSelected
                    ? "border-orange-500 ring-2 ring-orange-500/40 shadow-md"
                    : "border-border hover:border-orange-500 hover:ring-2 hover:ring-orange-500/40 hover:shadow-sm md:hover:-translate-y-0.5",
                )}
              >
                <img
                  src={url}
                  alt={`preview ${idx + 1}`}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {isSelected && (
                  <div className="absolute top-2 end-2 w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center shadow-md">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                )}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void downloadMedia(url, "talking-avatar-preview", "png");
                }}
                aria-label={t.talkingAvatar.previewDownloadAria}
                className="absolute bottom-2 start-2 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center transition-colors backdrop-blur-sm"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          );
        })}

        {/* Pending placeholders while generating */}
        {isGenerating &&
          imageUrls.length === 0 &&
          generationStartedAt &&
          Array.from({ length: 2 }).map((_, i) => (
            <PreviewSkeletonTile
              key={`pending-${i}`}
              startTime={generationStartedAt}
              aspectClass="aspect-[3/4]"
            />
          ))}

        {/* Cursor-hint animation: a pointer drifts between the two cards
            with a soft "tap" pulse to teach the user that they must pick one.
            Custom inline SVG (not Lucide) so we can render a filled-white body
            with a thick black outline that stays visible on any background. */}
        {showCursorHint && (
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none flex items-center justify-center"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-8 h-8 md:w-10 md:h-10 drop-shadow-[0_2px_6px_rgba(0,0,0,0.45)] animate-pick-hint"
            >
              <path
                d="M4 4l7.07 17 2.51-7.39L21 11.07 4 4z"
                fill="white"
                stroke="black"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}
